import React, { useState, useEffect, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import ModalSkeleton from "../../../../components/Modals/ModalSkeleton";
import CustomButton from "../../../../components/common/Button/CustomButton";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
import MultiSelectDropdown from '../../../../components/common/MultiSelectDropdown';
import MultiFileInput from "../../../../components/common/FileUpload/MultiFileInput";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {apiGetRequest, apiPatchRequest, apiDeleteRequest } from "../../../../helpers/Requests";
import { URL_FEEDBACK_MANAGERS_LIST, URL_FEEDBACK_WORKER_LIST} from "store/services/URL";
import { LoginContext } from "../../../../helpers/LoginContext";
import { uploadFilesOnVapour } from "../../../../Utils/uploadFileOnS3";
import SelectOption from "components/common/Select/Select";
import Loader from "../../../../components/common/Loader/Loader";
import {
  baseURLImage,
  getFileFromServerAsFileType,
  getExtension,
} from "../../../../Utils/utils";
import {
  FEEDBACK_STATUS_OPTIONS,
} from "../../../../store/constants";
import useCareLabels from "hooks/useCareLabels";
import CareLabelSkeleton from "components/common/CareLabelSkeleton/CareLabelSkeleton";

const EditFeedbackModal = ({
  isOpen,
  onClose,
  feedbackToEdit,
  onSuccess,
  refreshFeedback,
}) => {
  const { participantLabelPlural, isLoading: isCareLabelsLoading } = useCareLabels();
  const { isUserGracePeriodOver } = useContext(LoginContext);
  const [isUpdating, setIsUpdating] = useState(false);
  const [managerList, setManagerList] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [participantsList, setParticipantsList] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [workersList, setWorkersList] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [statusFilter, setStatusFilter] = useState({ value: 'positive', label: 'Positive' });
  const [fetchServerFiles, setFetchServerFiles] = useState(false);
  const [isFileDeleteLoader, setIsFileDeleteLoader] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      date: null,
      subject: "",
      email: [],
      details: "",
      attachments: [],
      participants: [],
      workers: [],
      status: { value: 'positive', label: 'Positive' }
    }
  });

  const watchFormData = watch();

  useEffect(() => {
    if (feedbackToEdit && isOpen) {
      setFilesData([]);
      async function fetchAttachments() {
        try {
          if (!feedbackToEdit.attachments || feedbackToEdit.attachments.length === 0) {
            reset({
              date: feedbackToEdit?.date || null,
              subject: feedbackToEdit?.subject || "",
              email: [],
              details: feedbackToEdit?.details || "",
              attachments: [],
              participants: [],
              workers: [],
              status: feedbackToEdit?.feedback_assessment ?
                FEEDBACK_STATUS_OPTIONS?.find(opt => opt.value === feedbackToEdit.feedback_assessment) ||
                {
                  value: feedbackToEdit.feedback_assessment,
                  label: feedbackToEdit.feedback_assessment.charAt(0).toUpperCase() + feedbackToEdit.feedback_assessment.slice(1)
                } :
                { value: 'positive', label: 'Positive' },
            });
            return;
          }

          setFetchServerFiles(true);
          const serverFiles = await Promise.all(
            feedbackToEdit.attachments?.map(async (item) => {
              const fileName = item?.original_name || item?.file_name || item?.name || 'attachment';
              const file = item?.file_path || item?.attachment_path;

              try {
                const response = await getFileFromServerAsFileType(
                  baseURLImage(file),
                  fileName
                );
                if (response && response instanceof File) {
                  try {
                    Object.defineProperty(response, 'name', {
                      writable: true,
                      value: fileName
                    });
                  } catch (nameError) {
                    console.warn(nameError);
                  }
                }

                return {
                  file: response,
                  ...item,
                  original_name: fileName,
                  file_name: fileName
                };
              } catch (error) {
                return { file: null, ...item };
              }
            }) || []
          );
          setFetchServerFiles(false);

          reset({
            date: feedbackToEdit?.date || null,
            subject: feedbackToEdit?.subject || "",
            email: [],
            details: feedbackToEdit?.details || "",
            attachments: serverFiles.filter((file) => file?.file !== null) || [],
            participants: [],
            workers: [],
            status: feedbackToEdit?.feedback_assessment ?
              FEEDBACK_STATUS_OPTIONS?.find(opt => opt?.value === feedbackToEdit?.feedback_assessment) ||
              {
                value: feedbackToEdit.feedback_assessment,
                label: feedbackToEdit.feedback_assessment?.charAt(0).toUpperCase() + feedbackToEdit?.feedback_assessment.slice(1)
              } :
              { value: 'positive', label: 'Positive' },
          });
        } catch (error) {
          setFetchServerFiles(false);
          toast.error("Error loading attachments.");
        }
      }

      fetchAttachments();
      fetchManagersList();
      fetchParticipantsList();
      fetchWorkersList();
    }
  }, [feedbackToEdit, isOpen]);

  const fetchManagersList = async () => {
    setLoadingManagers(true);
    try {
      const response = await apiGetRequest(URL_FEEDBACK_MANAGERS_LIST);
      if (response?.status === 200) {
        const formattedManagers = response?.data?.data?.map(manager => ({
          id: manager?.user?.id,
          uuid: manager?.user?.uuid,
          value: manager?.user?.email || manager?.user?.id,
          label: `${manager?.user?.name}`,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setManagerList(formattedManagers || []);

        if (feedbackToEdit?.mention_managers && Array.isArray(feedbackToEdit?.mention_managers)) {
          const selectedManagers = feedbackToEdit?.mention_managers.map(manager => {
            const found = formattedManagers?.find(m =>
              m?.uuid === manager?.uuid ||
              m?.label === manager?.name
            );
            return found || {
              value: manager?.uuid,
              label: manager?.name,
              uuid: manager?.uuid
            };
          }).filter(Boolean);
          setValue('email', selectedManagers);
        }
      }
    } catch (error) {
      setManagerList([]);
      toast.warn("Could not load managers list.");
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchParticipantsList = async () => {
    setLoadingParticipants(true);
    try {
      const response = await apiGetRequest("/sp/feedback/participants_list");
      if (response?.status === 200) {
        const formattedParticipants = response?.data?.data?.map((participant) => ({
          id: participant?.id,
          value: participant?.uuid || '',
          label: participant?.first_name + " " + participant?.last_name || '',
        })).sort((a, b) => a.label.localeCompare(b.label));
        setParticipantsList(formattedParticipants || []);

        if (feedbackToEdit?.participants && Array.isArray(feedbackToEdit.participants)) {
          const selectedParticipants = feedbackToEdit?.participants?.map(participant => {
            const found = formattedParticipants?.find(p => p?.value === participant?.uuid || p?.value === participant?.value);
            return found || {
              id: participant?.id,
              value: participant?.uuid || participant?.value,
              label: participant?.name || participant?.label
            };
          }).filter(Boolean);
          setValue('participants', selectedParticipants);
        }
      }
    } catch (error) {
      setParticipantsList([]);
      toast.warn("Could not load participants list.");
    } finally {
      setLoadingParticipants(false);
    }
  };

  const fetchWorkersList = async () => {
    setLoadingWorkers(true);
    try {
      const response = await apiGetRequest(URL_FEEDBACK_WORKER_LIST);
      if (response?.status === 200) {
        const formattedWorkers = response?.data?.data?.map((worker) => ({
          id: worker?.id,
          value: worker?.uuid || worker?.id,
          label: `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim() || worker?.name,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setWorkersList(formattedWorkers || []);

        if (feedbackToEdit?.workers && Array.isArray(feedbackToEdit?.workers)) {
          const selectedWorkers = feedbackToEdit?.workers?.map(worker => {
            const found = formattedWorkers?.find(w => w?.value === worker?.uuid || w?.value === worker?.value);
            return found || {
              id: worker?.id,
              value: worker?.uuid || worker?.value,
              label: worker?.name || worker?.label
            };
          }).filter(Boolean);
          setValue('workers', selectedWorkers);
        }
      }
    } catch (error) {
      setWorkersList([]);
      toast.warn("Could not load workers list.");
    } finally {
      setLoadingWorkers(false);
    }
  };

  const handleEditSubmit = async (data) => {
    if (isUpdating || isUserGracePeriodOver) return;

    setIsUpdating(true);
    try {
      const fileToUpload = data?.attachments?.filter(
        (fl) => fl instanceof File && !fl.id
      );

      let uploadedFiles = [];

      if (fileToUpload?.length > 0) {
        const uploadResults = await uploadFilesOnVapour(fileToUpload);
        uploadedFiles = uploadResults
          ?.filter((item) => item !== null)
          ?.map((item) => ({
            key: item?.key,
            ext: item?.ext,
            name: item?.name,
          })) || [];
      }

      const managerEmails = data?.email?.map(item => ({
        id: item?.id,
        name: item?.label,
        email: item?.value
      })) || [];

      const participantData = data?.participants?.map(participant => ({
        id: participant?.id,
        uuid: participant?.value,
        name: participant?.label
      })) || [];

      const workerData = data?.workers?.map(worker => ({
        id: worker?.id,
        uuid: worker?.value,
        name: worker?.label
      })) || [];

      const updatePayload = {
        date: data?.date,
        subject: data?.subject,
        email: managerEmails,
        details: data?.details,
        type: "feedback",
        attachments: uploadedFiles,
        participants: participantData,
        workers: workerData,
        feedback_assessment: data?.status?.value || 'positive',
      };
      const response = await apiPatchRequest(`/sp/feedback/update/${feedbackToEdit?.uuid}`, updatePayload);
      toast.success("Feedback updated successfully!");

      if (onSuccess) {
        onSuccess();
      }
      if (refreshFeedback) {
        await refreshFeedback();
      }
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message ||
                          "Failed to update feedback.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteItemCall = async (item, callback) => {

    if (isFileDeleteLoader || !item?.id) return;
    try {
      setIsFileDeleteLoader(item?.id ? item?.file?.name : item?.name);

      const response = await apiDeleteRequest(`/sp/feedback/attachment/${feedbackToEdit?.uuid}/${item.id}`);

      if (response?.status === 200 || response?.status === 204) {
        const currentAttachments = watchFormData?.attachments || [];
        const updatedAttachments = currentAttachments.filter(
          (attachment) => attachment.id !== item.id
        );
        setValue("attachments", updatedAttachments);

        if (callback) {
          callback();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete attachment");
    } finally {
      setIsFileDeleteLoader(null);
    }
  };

  const handleClose = () => {
    reset();
    setManagerList([]);
    setParticipantsList([]);
    setWorkersList([]);
    setFilesData([]);
    onClose();
  };

  const modalFooter = () => (
    <div className="flex space-x-2">
      <CustomButton
        type='submit'
        label='Update'
        showLoading={isUpdating}
        isDisabled={isUpdating || isUserGracePeriodOver || fetchServerFiles || isFileDeleteLoader}
        clickHandler={() => {
          if (isUserGracePeriodOver) return;
          const buttonRef = document?.getElementById("editFeedbackBtn");
          if (buttonRef) {
            buttonRef.click();
          }
        }}
      />
       <CustomButton
        clickHandler={handleClose}
        type='button'
        label='Close'
        variant='danger'
        isDisabled={isUpdating || isFileDeleteLoader || fetchServerFiles}
      />
    </div>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title='Edit Feedback'
      cssClass='w-[50%]'
      closeModal={handleClose}
      modalFooter={modalFooter()}
    >
      <div>
        <div className='flex flex-col'>
          <p className='text-sm text-gray-500 mb-3'>
            Update the feedback information
          </p>
          {feedbackToEdit?.uuid && (
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 mb-3 w-fit'>
              ID: {feedbackToEdit.uuid.substring(0, 8).toUpperCase()}
            </span>
          )}
        </div>
        <form onSubmit={handleSubmit(handleEditSubmit)} className=''>
          <div className='grid grid-cols-2 gap-x-4'>
            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>
                {isCareLabelsLoading ? (
                  <CareLabelSkeleton width="w-28" height="h-3.5" />
                ) : (
                  participantLabelPlural
                )}
              </label>
              <MultiSelectDropdown
                showIcon
                data={participantsList}
                cssClass='z-30'
                name='participants'
                register={register}
                setValue={setValue}
                control={control}
                watch={watch}
                isDisabled={loadingParticipants || isCareLabelsLoading}
                placeholder={
                  isCareLabelsLoading
                    ? "Select..."
                    : loadingParticipants
                    ? `Loading ${participantLabelPlural.toLowerCase()}...`
                    : `Select ${participantLabelPlural}`
                }
              />
            </div>

            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Workers</label>
              <MultiSelectDropdown
                showIcon
                data={workersList}
                cssClass='z-30'
                name='workers'
                register={register}
                setValue={setValue}
                control={control}
                watch={watch}
                isDisabled={loadingWorkers}
                placeholder={loadingWorkers ? "Loading workers..." : "Select Workers"}
              />
            </div>

            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Mention Manager</label>
              <MultiSelectDropdown
                showIcon
                data={managerList}
                cssClass='z-30'
                name='email'
                register={register}
                setValue={setValue}
                control={control}
                watch={watch}
                isDisabled={loadingManagers}
                placeholder={loadingManagers ? "Loading managers..." : "Select Managers"}
              />
              {errors?.email?.type === "required" && (
                <span className='text-sm text-red-500'>
                  At least one manager must be selected!
                </span>
              )}
            </div>

            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Type
                   <span className='text-red-500'>*</span>
              </label>
              <Controller
                name='status'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectOption
                    cssClass='mt-1'
                    isSearch={false}
                    data={FEEDBACK_STATUS_OPTIONS}
                    selectedOption={field.value}
                    handleChange={(e) => {
                      setStatusFilter(e);
                      field.onChange(e);
                    }}
                    placeholder="Select Status"
                    className='w-full'
                  />
                )}
              />
              {errors?.status?.type === "required" && (
                <span className='text-sm text-red-500'>
                  This field is required!
                </span>
              )}
            </div>

            <div className='mt-1 col-span-3 sm:col-span-1'>
              <label className='did-floating-label'>Date
                <span className='text-red-500'>*</span>
              </label>
              <Controller
                name='date'
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <DateRangeWithRegister
                    isSingleDate
                    field={field}
                    fieldState={fieldState}
                    placeholder='Select Date'
                    showCalendarIcon
                  />
                )}
              />
              {errors?.date?.type === "required" && (
                <span className='text-sm text-red-500'>
                  This field is required!
                </span>
              )}
            </div>

            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Subject
                <span className='text-red-500'>*</span>
              </label>
              <input
                {...register("subject", { required: true })}
                className='did-floating-input'
                type='text'
                maxLength={150}
              />
              {errors?.subject?.type === "required" && (
                <span className='text-sm text-red-500'>
                  This field is required!
                </span>
              )}
            </div>

            <div className='mt-1 col-span-2'>
              <label className='did-floating-label'>Details
                <span className='text-red-500'>*</span>
              </label>
              <Controller
                name="details"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CKEditor
                    editor={ClassicEditor}
                    data={field?.value || ''}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      field?.onChange(data);
                    }}
                    config={{
                      placeholder: "Enter feedback details here...",
                    }}
                  />
                )}
              />
              {errors?.details?.type === "required" && (
                <span className='text-sm text-red-500'>
                  This field is required!
                </span>
              )}
            </div>

            <div className='mt-4 col-span-2'>
              <label className='did-floating-label'>
                Attachments
              </label>
              <div>
                {fetchServerFiles ? (
                  <Loader isSmallView={true} cssClass={"text-black"} />
                ) : (
                  <MultiFileInput
                    type='feedback'
                    isFileUploadToS3={true}
                    handleFile={(e) => {
                      setValue("attachments", e);
                    }}
                    size={5}
                    files={watchFormData?.attachments}
                    handleDelete={(e) => {
                      setValue("attachments", e);
                    }}
                    handleDeleteItem={handleDeleteItemCall}
                    showDragAndDrop={false}
                    deleteFileLoader={isFileDeleteLoader}
                    isDisabled={isFileDeleteLoader || fetchServerFiles}
                    filenameLimit={150}
                    setFilesData={setFilesData}
                    setIsSubmitting={setIsUpdating}
                  />
                )}
              </div>
            </div>
          </div>
          <button
            type='submit'
            className='hidden sr-only'
            id='editFeedbackBtn'
          />
        </form>
      </div>
    </ModalSkeleton>
  );
};

export default EditFeedbackModal;
